import { zodResolver } from '@hookform/resolvers/zod'
import { Grid } from '@mui/joy'
import { ReactNode, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FlowStatement, userOptionsStore } from '~/shared/api'
import {
  transactionBalanceTypeEnumLabels,
  transactionBalanceTypeEnumOptions,
  transactionPaymentTypeEnumLabels,
  transactionPaymentTypeEnumOptions,
} from '~/shared/config/constants'
import {
  TransactionBalanceTypeEnum,
  TransactionPaymentTypeEnum,
} from '~/shared/config/enums'
import {
  AsyncAutocompleteInput,
  AutocompleteInput,
  FormButtons,
  FormProvider,
} from '~/shared/ui/Form'
import { Container, ContainerTitle } from '~/shared/ui/Layout'
import { FormValues, formSchema } from './model'

type FormProps = {
  onSuccess?: (formValues: FormValues) => void
  isSubmitting: boolean
  children: ReactNode
}

export function Form({ onSuccess, isSubmitting, children }: FormProps) {
  const { t } = useTranslation()

  const balanceTypeOptions = useMemo(() => {
    const allowedBalanceTypes: TransactionBalanceTypeEnum[] = [
      TransactionBalanceTypeEnum.MAIN,
      TransactionBalanceTypeEnum.BY_BONUS,
      ...(userOptionsStore.depositBalanceEnabled
        ? [TransactionBalanceTypeEnum.BY_DEPOSIT]
        : []),
    ]

    return transactionBalanceTypeEnumOptions.filter((o) =>
      allowedBalanceTypes.includes(o.id),
    )
  }, [])

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      paymentType: {
        id: TransactionPaymentTypeEnum.NON_CASH,
        label:
          transactionPaymentTypeEnumLabels[TransactionPaymentTypeEnum.NON_CASH],
      },
      balanceType: {
        id: TransactionBalanceTypeEnum.MAIN,
        label:
          transactionBalanceTypeEnumLabels[TransactionBalanceTypeEnum.MAIN],
      },
    },
  })

  return (
    <FormProvider form={form} onSuccess={onSuccess}>
      <Container>
        <ContainerTitle>{t('general information')}</ContainerTitle>
        <Grid container spacing={2.5}>
          <Grid xs={6}>
            <AsyncAutocompleteInput
              label='form.turnover item'
              placeholder='form.select'
              name='flowStatementId'
              fetchOptions={FlowStatement.fetchOptions}
            />
          </Grid>
          <Grid xs={6}>
            <AutocompleteInput
              label='form.payment method'
              placeholder='form.select'
              name='paymentType'
              options={transactionPaymentTypeEnumOptions}
            />
          </Grid>
          <Grid xs={6}>
            <AutocompleteInput
              label='form.balance type'
              placeholder='form.select'
              name='balanceType'
              options={balanceTypeOptions}
            />
          </Grid>
        </Grid>
      </Container>

      {children}

      <FormButtons loading={isSubmitting} isCreating />
    </FormProvider>
  )
}
